import React, { useState, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { Provider } from 'react-redux';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { ImageryProvider } from '@appcues/imagery-kit';
import { ThemeEditorProvider } from '@appcues/theme-editor';
import AnalyticsContext, { createAnalyticsClient } from 'ext/lib/analytics';
import { Shape as AuthShape } from 'lib/auth';
import UserShape from 'entities/user/shape';
import StyleProvider from 'styles/StyleProvider';
import configureStore from './configure-store';
import rootReducer from './root-reducer';
import rootSaga from './root-saga';

export default function Providers({ auth, user, children }) {
  const [store, setStore] = useState(null);

  useLayoutEffect(() => {
    (async () => {
      const configuredStore = await configureStore({
        auth,
        user,
        rootReducer,
        rootSaga,
      });
      setStore(configuredStore);
    })();
  }, [auth, user]);

  // return null while store has been created, since now it configureStore is async
  if (!store) {
    return null;
  }

  const analyticsClient = createAnalyticsClient(
    store.getState(),
    {
      app: 'Mobile Builder',
    },
    true // client mode
  );

  const apiProps = {
    jwt: auth.jwt,
    accountId: auth.accountId,
    customerApiURL: import.meta.env.CUSTOMER_API_URL,
  };

  return (
    <Provider store={store}>
      <AnalyticsContext.Provider value={analyticsClient}>
        <DndProvider backend={HTML5Backend}>
          <StyleProvider>
            {/* eslint-disable-next-line @appcues/jsx-props-no-spreading */}
            <ImageryProvider {...apiProps}>
              {/* eslint-disable-next-line @appcues/jsx-props-no-spreading */}
              <ThemeEditorProvider {...apiProps}>
                {children}
              </ThemeEditorProvider>
            </ImageryProvider>
          </StyleProvider>
        </DndProvider>
      </AnalyticsContext.Provider>
    </Provider>
  );
}

Providers.propTypes = {
  auth: AuthShape,
  user: UserShape,
  children: PropTypes.node.isRequired,
};
